import React from 'react';
import { Button, Alert } from '../ui';
import { formatarPreco } from '../../utils/priceUtils';
import { formatWhatsApp } from '../../utils/formatUtils';

const MobileBookingSummary = ({
  success,
  appointmentData,
  services,
  professionals,
  onNewBooking,
  onClose,
  error
}) => {
  if (!success) {
    return (
      <div className="flex flex-col items-center justify-center min-h-64 p-4">
        <Alert type="error" title="Erro no agendamento" className="mb-6 w-full">
          {error || 'Ocorreu um erro ao processar seu agendamento. Por favor, tente novamente.'}
        </Alert>
        
        <div className="flex gap-3 w-full">
          <Button onClick={onClose} variant="primary" className="w-full">
            Fechar
          </Button>
        </div>
      </div>
    );
  }

  if (!appointmentData) {
    return (
      <div className="p-4 text-center">
        <p className="text-gray-500">Dados do agendamento não disponíveis.</p>
        <Button onClick={onClose} variant="primary" className="mt-4">
          Fechar
        </Button>
      </div>
    );
  }

  const { 
    cliente, 
    whatsapp, 
    data, 
    dataFormatada, 
    horario, 
    servicos = [], 
    profissional, 
    professionalCustomPrices = {}, 
    servicosDetalhes = null 
  } = appointmentData;
  
  // Obter detalhes dos serviços selecionados
  const getDetalhesServicos = () => {
    // Se já temos os detalhes dos serviços com preços customizados, usar esses
    if (servicosDetalhes && servicosDetalhes.length > 0) {
      return servicosDetalhes;
    }
    
    // Caso contrário, montar a partir dos serviços e profissional
    return servicos.map(servicoId => {
      const servico = services.find(s => s.id === servicoId);
      if (!servico) return { nome: 'Serviço não encontrado', preco: 0, duracao: 0 };
      
      // Verificar se tem preço personalizado
      const customPrice = professionalCustomPrices[servicoId];
      
      return {
        ...servico,
        preco: customPrice ? customPrice.price : servico.preco
      };
    });
  };
  
  const servicosComPrecos = getDetalhesServicos();
  
  // Calcular preço total
  const precoTotal = servicosComPrecos.reduce((total, servico) => total + (servico.preco || 0), 0);
  
  // Calcular duração total
  const duracaoTotal = appointmentData.duracaoTotal || 
    servicosComPrecos.reduce((total, servico) => total + (servico.duracao || 0), 0);
  
  // Obter detalhes do profissional selecionado
  const profissionalDetalhe = professionals.find(p => p.id === profissional) || {};
  
  // Formatação de duração
  const formatDuration = (minutes) => {
    if (minutes < 60) {
      return `${minutes} min`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  return (
    <div className="space-y-5 px-1 pb-16">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 text-green-600 mb-4">
          <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Agendamento confirmado!</h2>
        <p className="text-gray-600">
          Enviamos os detalhes para o WhatsApp {formatWhatsApp(whatsapp)}.
        </p>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-blue-50 px-4 py-3 border-b border-blue-100">
          <h3 className="text-lg font-medium text-blue-800">Detalhes do agendamento</h3>
        </div>
        
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            <div>
              <p className="text-xs text-gray-500">Cliente</p>
              <p className="font-medium">{cliente}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Contato</p>
              <p className="font-medium">{formatWhatsApp(whatsapp)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Data</p>
              <p className="font-medium">{dataFormatada || data}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Horário</p>
              <p className="font-medium">{horario}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Profissional</p>
              <p className="font-medium">{profissionalDetalhe.nome || "Não especificado"}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Duração</p>
              <p className="font-medium">{formatDuration(duracaoTotal)}</p>
            </div>
          </div>
          
          <div className="border-t pt-4 mt-2">
            <p className="text-xs font-medium text-gray-700 mb-2">Serviços</p>
            <ul className="divide-y">
              {servicosComPrecos.map((servico, index) => (
                <li key={index} className="py-2 flex justify-between">
                  <span className="text-sm">{servico.nome}</span>
                  <span className="text-sm font-medium">{formatarPreco(servico.preco)}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-between font-semibold text-lg border-t pt-4 mt-2">
              <span>Total</span>
              <span>{formatarPreco(precoTotal)}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Barra inferior fixa com botões */}
      <div className="fixed bottom-0 left-0 right-0 bg-white p-4 shadow-lg border-t border-gray-200 z-10">
        <Button 
          onClick={onClose} 
          variant="primary"
          className="w-full"
        >
          Fechar
        </Button>
      </div>
    </div>
  );
};

export default MobileBookingSummary; 